import { type Component, createSignal, createMemo, onMount, Show, For } from 'solid-js';
import { useTranslation } from '@/i18n';

interface MovementDiagramProps {
  optimalRoute: number[];
  teamRoute: number[];
  allTiles: number[];
  routeEfficiency: number | null;
  solveTimes?: Array<{
    tile_number: number;
    minutes_from_start: number | null;
    attempts: number;
  }>;
  teamName: string;
  gameName: string;
  finalTime?: string;
  onClose?: () => void;
}

interface Point {
  x: number;
  y: number;
  tile: number;
  angle: number;
}

/**
 * MovementDiagram - Circular graph visualization showing team's route
 *
 * Option B implementation: Points arranged in a circle, lines showing solve order
 * - Gray lines: optimal route
 * - Colored gradient lines: team's actual route
 * - Numbers indicate solve order
 */
const MovementDiagram: Component<MovementDiagramProps> = (props) => {
  const { t } = useTranslation();
  const [showOptimal, setShowOptimal] = createSignal(true);
  const [showTeam, setShowTeam] = createSignal(true);
  const [hoveredTile, setHoveredTile] = createSignal<number | null>(null);

  // SVG dimensions
  const size = 400;
  const center = size / 2;
  const radius = 160;
  const nodeRadius = 18;

  // Calculate positions for all tiles in a circle
  const tilePositions = createMemo((): Map<number, Point> => {
    const positions = new Map<number, Point>();
    const tiles = props.allTiles;
    const count = tiles.length;

    tiles.forEach((tile, index) => {
      // Start from top (-90 degrees) and go clockwise
      const angle = ((index / count) * 2 * Math.PI) - (Math.PI / 2);
      const x = center + radius * Math.cos(angle);
      const y = center + radius * Math.sin(angle);
      positions.set(tile, { x, y, tile, angle });
    });

    return positions;
  });

  // Get position for a tile
  const getPos = (tile: number): Point | undefined => tilePositions().get(tile);

  // Generate path for a route
  const generateRoutePath = (route: number[]): string => {
    if (route.length < 2) return '';

    const points = route.map(tile => getPos(tile)).filter(Boolean) as Point[];
    if (points.length < 2) return '';

    return points.map((p, i) =>
      i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`
    ).join(' ');
  };

  // Calculate solve order for each tile
  const solveOrderMap = createMemo((): Map<number, number> => {
    const map = new Map<number, number>();
    props.teamRoute.forEach((tile, index) => {
      map.set(tile, index + 1);
    });
    return map;
  });

  // Get solve time info for a tile
  const getSolveInfo = (tile: number) => {
    return props.solveTimes?.find(s => s.tile_number === tile);
  };

  // Efficiency color
  const efficiencyColor = createMemo(() => {
    const eff = props.routeEfficiency;
    if (eff === null) return '#6b7280';
    if (eff >= 80) return '#22c55e';
    if (eff >= 60) return '#eab308';
    if (eff >= 40) return '#f97316';
    return '#ef4444';
  });

  // Generate gradient ID for team route
  const teamGradientId = 'team-route-gradient';

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div class="relative w-full max-w-2xl animate-scale-in">
        <div class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Header */}
          <div class="px-6 pt-6 pb-4 border-b border-white/10">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-xl font-bold text-white">
                  {t('movementDiagram.title')}
                </h2>
                <p class="text-sm text-white/60 mt-1">
                  {props.teamName} • {props.gameName}
                </p>
              </div>
              <Show when={props.onClose}>
                <button
                  onClick={props.onClose}
                  class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <span class="text-white/70">✕</span>
                </button>
              </Show>
            </div>

            {/* Stats row */}
            <div class="flex gap-4 mt-4">
              <Show when={props.routeEfficiency !== null}>
                <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <span class="text-sm text-white/60">{t('movementDiagram.efficiency')}:</span>
                  <span
                    class="text-lg font-bold"
                    style={{ color: efficiencyColor() }}
                  >
                    {props.routeEfficiency}%
                  </span>
                </div>
              </Show>
              <Show when={props.finalTime}>
                <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <span class="text-sm text-white/60">{t('movementDiagram.time')}:</span>
                  <span class="text-lg font-bold text-emerald-400">
                    {props.finalTime}
                  </span>
                </div>
              </Show>
            </div>
          </div>

          {/* Diagram */}
          <div class="p-6">
            {/* Legend / Toggle */}
            <div class="flex justify-center gap-4 mb-4">
              <Show when={props.optimalRoute.length > 0}>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOptimal()}
                    onChange={(e) => setShowOptimal(e.target.checked)}
                    class="w-4 h-4 rounded border-gray-600 bg-gray-700 text-gray-400"
                  />
                  <span class="flex items-center gap-1.5 text-sm text-white/70">
                    <span class="w-4 h-0.5 bg-gray-500"></span>
                    {t('movementDiagram.optimalRoute')}
                  </span>
                </label>
              </Show>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTeam()}
                  onChange={(e) => setShowTeam(e.target.checked)}
                  class="w-4 h-4 rounded border-emerald-600 bg-emerald-900 text-emerald-500"
                />
                <span class="flex items-center gap-1.5 text-sm text-white/70">
                  <span class="w-4 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500"></span>
                  {t('movementDiagram.teamRoute')}
                </span>
              </label>
            </div>

            {/* SVG Diagram */}
            <div class="flex justify-center">
              <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                class="max-w-full h-auto"
              >
                <defs>
                  {/* Gradient for team route */}
                  <linearGradient id={teamGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stop-color="#22c55e" />
                    <stop offset="50%" stop-color="#06b6d4" />
                    <stop offset="100%" stop-color="#8b5cf6" />
                  </linearGradient>

                  {/* Arrow marker */}
                  <marker
                    id="arrow-team"
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="4"
                    markerHeight="4"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#06b6d4" />
                  </marker>
                  <marker
                    id="arrow-optimal"
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="4"
                    markerHeight="4"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#6b7280" />
                  </marker>
                </defs>

                {/* Optimal route lines (background) */}
                <Show when={showOptimal() && props.optimalRoute.length > 1}>
                  <g class="optimal-route">
                    <For each={props.optimalRoute.slice(0, -1)}>
                      {(tile, idx) => {
                        const from = getPos(tile);
                        const to = getPos(props.optimalRoute[idx() + 1]);
                        if (!from || !to) return null;

                        return (
                          <line
                            x1={from.x}
                            y1={from.y}
                            x2={to.x}
                            y2={to.y}
                            stroke="#4b5563"
                            stroke-width="2"
                            stroke-dasharray="6,4"
                            opacity="0.6"
                          />
                        );
                      }}
                    </For>
                  </g>
                </Show>

                {/* Team route lines (foreground) */}
                <Show when={showTeam() && props.teamRoute.length > 1}>
                  <g class="team-route">
                    <For each={props.teamRoute.slice(0, -1)}>
                      {(tile, idx) => {
                        const from = getPos(tile);
                        const to = getPos(props.teamRoute[idx() + 1]);
                        if (!from || !to) return null;

                        // Calculate color based on position in route
                        const progress = idx() / (props.teamRoute.length - 1);
                        const hue = 140 + progress * 100; // Green to cyan to purple

                        return (
                          <line
                            x1={from.x}
                            y1={from.y}
                            x2={to.x}
                            y2={to.y}
                            stroke={`hsl(${hue}, 70%, 50%)`}
                            stroke-width="3"
                            stroke-linecap="round"
                            marker-end="url(#arrow-team)"
                          />
                        );
                      }}
                    </For>
                  </g>
                </Show>

                {/* Tile nodes */}
                <For each={props.allTiles}>
                  {(tile) => {
                    const pos = getPos(tile);
                    if (!pos) return null;

                    const isSolved = props.teamRoute.includes(tile);
                    const solveOrder = solveOrderMap().get(tile);
                    const isHovered = hoveredTile() === tile;
                    const solveInfo = getSolveInfo(tile);

                    return (
                      <g
                        class="tile-node cursor-pointer"
                        onMouseEnter={() => setHoveredTile(tile)}
                        onMouseLeave={() => setHoveredTile(null)}
                      >
                        {/* Node circle */}
                        <circle
                          cx={pos.x}
                          cy={pos.y}
                          r={isHovered ? nodeRadius + 4 : nodeRadius}
                          fill={isSolved ? '#059669' : '#374151'}
                          stroke={isSolved ? '#34d399' : '#6b7280'}
                          stroke-width={isHovered ? 3 : 2}
                          class="transition-all duration-200"
                        />

                        {/* Tile number */}
                        <text
                          x={pos.x}
                          y={pos.y}
                          dy="0.35em"
                          text-anchor="middle"
                          fill="white"
                          font-size="12"
                          font-weight="bold"
                        >
                          {tile}
                        </text>

                        {/* Solve order badge */}
                        <Show when={isSolved && solveOrder}>
                          <g>
                            <circle
                              cx={pos.x + nodeRadius - 2}
                              cy={pos.y - nodeRadius + 2}
                              r="10"
                              fill="#8b5cf6"
                              stroke="#c4b5fd"
                              stroke-width="1"
                            />
                            <text
                              x={pos.x + nodeRadius - 2}
                              y={pos.y - nodeRadius + 2}
                              dy="0.35em"
                              text-anchor="middle"
                              fill="white"
                              font-size="9"
                              font-weight="bold"
                            >
                              {solveOrder}
                            </text>
                          </g>
                        </Show>

                        {/* Hover tooltip */}
                        <Show when={isHovered && solveInfo}>
                          <g>
                            <rect
                              x={pos.x - 50}
                              y={pos.y + nodeRadius + 8}
                              width="100"
                              height="36"
                              rx="6"
                              fill="#1f2937"
                              stroke="#374151"
                            />
                            <text
                              x={pos.x}
                              y={pos.y + nodeRadius + 22}
                              text-anchor="middle"
                              fill="#9ca3af"
                              font-size="10"
                            >
                              {solveInfo!.minutes_from_start !== null
                                ? `${solveInfo!.minutes_from_start} min`
                                : ''}
                            </text>
                            <text
                              x={pos.x}
                              y={pos.y + nodeRadius + 36}
                              text-anchor="middle"
                              fill="#6b7280"
                              font-size="9"
                            >
                              {solveInfo!.attempts} {solveInfo!.attempts === 1 ? 'próba' : 'prób'}
                            </text>
                          </g>
                        </Show>
                      </g>
                    );
                  }}
                </For>

                {/* Start indicator */}
                <Show when={props.teamRoute.length > 0}>
                  {(() => {
                    const startPos = getPos(props.teamRoute[0]);
                    if (!startPos) return null;
                    return (
                      <text
                        x={startPos.x}
                        y={startPos.y - nodeRadius - 8}
                        text-anchor="middle"
                        fill="#22c55e"
                        font-size="11"
                        font-weight="bold"
                      >
                        START
                      </text>
                    );
                  })()}
                </Show>

                {/* End indicator */}
                <Show when={props.teamRoute.length > 1}>
                  {(() => {
                    const endPos = getPos(props.teamRoute[props.teamRoute.length - 1]);
                    if (!endPos) return null;
                    return (
                      <text
                        x={endPos.x}
                        y={endPos.y + nodeRadius + 16}
                        text-anchor="middle"
                        fill="#8b5cf6"
                        font-size="11"
                        font-weight="bold"
                      >
                        META
                      </text>
                    );
                  })()}
                </Show>
              </svg>
            </div>

            {/* Summary */}
            <div class="mt-4 text-center">
              <p class="text-sm text-white/50">
                {t('movementDiagram.solvedCount', {
                  solved: props.teamRoute.length,
                  total: props.allTiles.length
                })}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div class="px-6 pb-6">
            <button
              onClick={props.onClose}
              class="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg hover:shadow-emerald-500/30 transition-all"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MovementDiagram;
